export type DetectionLanguage = 'javascript' | 'php' | 'python' | 'java' | 'sql' | 'other';

export interface LanguageScore {
  language: DetectionLanguage;
  score: number;
}

export interface LanguageDetectionResult {
  language: DetectionLanguage | null;
  confidence: number;
  reason?: string;
}

const PATTERNS: Record<DetectionLanguage, Array<{ regex: RegExp; points: number }>> = {
  javascript: [
    { regex: /\btypeerror\b|\breferenceerror\b|\bsyntaxerror\b|cannot read properties of undefined|is not a function|uncaught (typeerror|referenceerror|syntaxerror|in promise)/, points: 3 },
    { regex: /node_modules|npm err|webpack|vite|next\.js|react/, points: 2 },
    { regex: /\bjavascript\b|\bjs\b|\bnode\b|\breact\b/, points: 1 },
  ],
  php: [
    { regex: /\bphp (fatal )?error\b|\bphp warning\b|\bphp notice\b|\bundefined index\b|\bundefined variable\b|\bwarning:\b|\bnotice:\b|call to undefined function|uncaught exception|uncaught [a-z_\\]+exception|in .*\.php on line \d+/, points: 4 },
    { regex: /\$[a-z_][a-z0-9_]*/, points: 1 },
    { regex: /\bphp\b|\.php\b|\$_(get|post|server|session|cookie)\b/, points: 2 },
  ],
  python: [
    { regex: /\btraceback \(most recent call last\):|\bindexerror\b|\bkeyerror\b|\battributeerror\b|\bmodulenotfounderror\b|\bindentationerror\b/, points: 3 },
    { regex: /^\s*file ".+", line \d+/m, points: 2 },
  ],
  java: [
    { regex: /exception in thread|\bnullpointerexception\b|\bjava\.lang\.|\bat [a-z0-9_$.]+\([a-z0-9_$.]+:\d+\)/i, points: 3 },
    { regex: /\bclasspath\b|\bjvm\b/, points: 1 },
  ],
  sql: [
    { regex: /\bsqlstate\b|syntax error at or near|relation .* does not exist|column .* does not exist|ora-\d+|mysql|postgres/, points: 3 },
    { regex: /\bselect\b.+\bfrom\b|\bwhere\b|\bjoin\b/, points: 1 },
  ],
  other: [],
};

export function scoreLanguages(input: string): LanguageScore[] {
  const text = input.toLowerCase();
  const scores: Record<DetectionLanguage, number> = {
    javascript: 0,
    php: 0,
    python: 0,
    java: 0,
    sql: 0,
    other: 0,
  };

  if (!text.trim()) {
    return Object.entries(scores).map(([language, score]) => ({ language: language as DetectionLanguage, score }));
  }

  for (const [language, patterns] of Object.entries(PATTERNS)) {
    const lang = language as DetectionLanguage;
    let total = 0;
    for (const { regex, points } of patterns) {
      if (regex.test(input)) {
        total += points;
      }
    }
    scores[lang] = total;
  }

  // Disambiguation
  const hasPhpSignal = /\bphp\b|\.php\b|\$_(get|post|server|session|cookie)\b/.test(text);
  const hasJsSignal = /\bjavascript\b|\bjs\b|\bnode\b|\breact\b/.test(text);
  if (hasPhpSignal) scores.javascript = Math.max(0, scores.javascript - 2);
  if (hasJsSignal) scores.php = Math.max(0, scores.php - 1);

  return (Object.entries(scores) as Array<[DetectionLanguage, number]>)
    .map(([language, score]) => ({ language, score }))
    .sort((a, b) => b.score - a.score);
}

export function detectLanguage(
  input: string,
  context?: {
    hintLanguage?: DetectionLanguage | null;
    selectedLanguage?: DetectionLanguage;
    hintWeight?: number;
    selectedWeight?: number;
  }
): LanguageDetectionResult {
  if (!input.trim()) {
    return { language: null, confidence: 0, reason: 'Empty input' };
  }

  const scored = scoreLanguages(input);

  // Apply context priors
  if (context?.hintLanguage && context.hintLanguage !== 'other') {
    const idx = scored.findIndex((s) => s.language === context.hintLanguage);
    if (idx >= 0) {
      scored[idx].score += context.hintWeight ?? 2;
      scored.sort((a, b) => b.score - a.score);
    }
  }

  if (context?.selectedLanguage && context.selectedLanguage !== 'other') {
    const idx = scored.findIndex((s) => s.language === context.selectedLanguage);
    if (idx >= 0) {
      scored[idx].score += context.selectedWeight ?? 1;
      scored.sort((a, b) => b.score - a.score);
    }
  }

  const [best, second] = scored;
  const confidence = best.score - (second?.score ?? 0);

  if (best.score < 2 || confidence < 1) {
    return { language: null, confidence: 0, reason: 'Low signal from classifier' };
  }

  return {
    language: best.language === 'other' ? null : best.language,
    confidence: Math.min(0.95, 0.4 + confidence * 0.15),
    reason: 'Pattern-based heuristic classification',
  };
}
